import ExamImage from "../components/ExamImage";
import React from "react";
import FeedbackList from "./FeedbackList";
import InstructionAudioButton from "./InstructionAudioButton";
import useHorizontalOverflow from "./useHorizontalOverflow";

export const QuestionInstructionAudioContext = React.createContext(null);

export default function QuestionLayout({
  number,
  prompt,
  points,
  children,
  feedback,
  mode,
  legendImage,
  hideFB = false,
  hideNumber = false,   // ⭐ NEW
  hidePoints = false,  // ⭐ NEW
  promptSize = "normal",
  horizontalScrollRef = null,
}) {
  const instructionAudio = React.useContext(QuestionInstructionAudioContext);
  const contentRef = React.useRef(null);
  const overflowRef = horizontalScrollRef || contentRef;
  const { hasOverflow, hasMoreToRight } = useHorizontalOverflow(overflowRef);

  return (
    <>
      <div
        tabIndex={0}
        aria-label={`Question ${number}`}
        style={{
          ...styles.container,
          gridTemplateColumns: legendImage
            ? "minmax(0, 1fr) 220px 70px"
            : "minmax(0, 1fr) 70px",
        }}
      >
        <div style={styles.main}>
          <div
            style={{
              ...(promptSize === "compact"
                ? styles.promptCompact
                : styles.prompt)
            }}
          >
            <div style={styles.promptHeader}>
              {!hideNumber && number != null && (
                <div style={styles.numberAudioGroup}>
                  <span style={styles.promptNumber}>{number}.</span>
                  {instructionAudio && (
                    <InstructionAudioButton
                      src={instructionAudio}
                      label={`Play instructions for question ${number}`}
                    />
                  )}
                </div>
              )}
              <div style={styles.promptText}>
                {renderPrompt(prompt)}
              </div>
            </div>
          </div>

          <div style={styles.contentWrap}>
            <div
              ref={contentRef}
              style={{
                ...styles.content,
                ...(horizontalScrollRef ? styles.contentWithChildScrollOwner : null)
              }}
              data-question-scroll-owner={horizontalScrollRef ? "false" : "true"}
            >
              {React.Children.map(children, (child, index) => (
                <div key={child?.key ?? index} style={styles.contentItem}>
                  {React.isValidElement(child)
                    ? child
                    : renderInline(child)}
                </div>
              ))}
            </div>

            {hasMoreToRight && (
              <div style={styles.rightOverflowFade} aria-hidden="true" />
            )}

            {hasOverflow && (
              <div style={styles.scrollHint} role="status">
                Scroll right to see the rest of this question →
              </div>
            )}

          </div>
        </div>

        {legendImage && (
          <div style={styles.legend}>
            <ExamImage src={legendImage} alt="Legend" style={{ width: "100%" }} />
          </div>
        )}

        {!hidePoints && !promptHasLinePoints(prompt) && (
          <div style={styles.points}>
            {renderInline(points)}
          </div>
        )}
      </div>

      {/* ✅ GLOBAL FEEDBACK (outside grid, consistent for all widgets) */}
      {mode === "review" && feedback && !hideFB && (
        <FeedbackList feedback={feedback} />
      )}
    </>
  );

}

/* ───────────────────── STYLES ───────────────────── */

const styles = {
  promptHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px"
  },

  numberAudioGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: "0 0 auto"
  },

  promptNumber: {
    fontWeight: "bold",
    whiteSpace: "nowrap"
  },

  promptText: {
    flex: 1,
    minWidth: 0
  },

  container: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 220px 70px",
    gap: "16px",
    alignItems: "start",
    marginBottom: "32px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0
  },

  main: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0
  },

  prompt: {
    fontSize: "18px",
    marginBottom: "12px",
    lineHeight: 1.4
  },

  promptCompact: {
    fontSize: "16px",      // ← matches World widgets
    marginBottom: "8px",
    lineHeight: 1.4
  },

  contentWrap: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0
  },

  content: {
    position: "relative",
    padding: "8px",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    boxSizing: "border-box"
  },

  contentWithChildScrollOwner: {
    // Some widgets (notably melodyInput/DurButton) must react to their own
    // scrollLeft. The child owns horizontal scrolling, so QuestionLayout must
    // never expose a second horizontal scrollbar around the same question.
    overflowX: "hidden"
  },

  contentItem: {
    minWidth: 0
  },

  rightOverflowFade: {
    position: "absolute",
    top: 1,
    right: 1,
    bottom: 31,
    width: 34,
    pointerEvents: "none",
    background:
      "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.96))"
  },

  scrollHint: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    minHeight: 28,
    marginTop: 4,
    padding: "3px 8px",
    borderRadius: 4,
    backgroundColor: "#f3f4f6",
    color: "#444",
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: "nowrap"
  },


  legend: {
    border: "1px solid #ccc",
    padding: "8px",
    backgroundColor: "white",
    minWidth: 0
  },

  points: {
    border: "2px solid #333",
    textAlign: "center",
    fontSize: "18px",
    height: "30px",
    width: "30px",
    lineHeight: "30px",
    justifySelf: "center"
  }
};

/* ───────────────────── TEXT RENDERING ───────────────────── */

function renderInline(content) {
  if (content == null || typeof content === "boolean") return null;

  if (typeof content === "string" || typeof content === "number") {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map((item, i) => (
      <React.Fragment key={i}>
        {renderInline(item)}
      </React.Fragment>
    ));
  }

  if (typeof content === "object") {
    if (content.bold)
      return <strong>{renderInline(content.bold)}</strong>;
    if (content.italic)
      return <em>{renderInline(content.italic)}</em>;
    if (content.underline)
      return (
        <span style={{ textDecoration: "underline" }}>
          {renderInline(content.underline)}
        </span>
      );
    if (content.code)
      return (
        <code style={{ fontSize: "0.95em" }}>
          {renderInline(content.code)}
        </code>
      );
  }

  return null;
}

/* ───────────────────── PROMPT HELPERS ───────────────────── */

function promptHasLinePoints(prompt) {
  if (!prompt || typeof prompt !== "object") return false;

  if (prompt.type === "multiline" && Array.isArray(prompt.lines)) {
    return prompt.lines.some(
      (line) => typeof line?.points === "number"
    );
  }

  if (prompt.type === "blocks" && Array.isArray(prompt.blocks)) {
    return prompt.blocks.some(
      (b) => typeof b?.points === "number"
    );
  }

  return false;
}

function renderPrompt(prompt) {
  if (!prompt) return null;

  /* 1️⃣ Plain string */
  if (typeof prompt === "string") {
    return <div>{prompt}</div>;
  }

  /* 2️⃣ Array → paragraphs */
  if (Array.isArray(prompt)) {
    return (
      <div>
        {prompt.map((p, i) => (
          <div key={i} style={{ marginBottom: "6px" }}>
            {renderInline(p)}
          </div>
        ))}
      </div>
    );
  }

  /* 2.5️⃣ Multiline */
  if (prompt.type === "multiline" && Array.isArray(prompt.lines)) {
    return (
      <div>
        {prompt.lines.map((line, i) => {
          const hasLabel = line?.label;
          const hasPoints = typeof line?.points === "number";

          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: hasPoints
                  ? "auto 1fr auto"
                  : "auto 1fr",
                columnGap: "8px",
                alignItems: "start",
                marginBottom: "6px"
              }}
            >
              {hasLabel && (
                <div style={{ fontWeight: "bold" }}>
                  {renderInline(line.label)}
                </div>
              )}

              <div>
                {renderInline(line.text ?? line)}
              </div>

              {hasPoints && (
                <div
                  style={{
                    border: "2px solid #333",
                    width: "26px",
                    height: "26px",
                    lineHeight: "26px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold"
                  }}
                >
                  {line.points}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /* 3️⃣ Structured blocks */
  if (prompt.type === "blocks" && Array.isArray(prompt.blocks)) {
    return (
      <div>
        {prompt.blocks.map((block, i) => {
          switch (block.type) {
            case "paragraph":
              return (
                <div key={i} style={{ marginBottom: "6px" }}>
                  {renderInline(block.content)}
                </div>
              );

            case "labeled":
              return (
                <div key={i} style={{ marginBottom: "6px" }}>
                  <strong>{renderInline(block.label)}</strong>{" "}
                  {renderInline(block.content)}
                </div>
              );

            case "list":
              return (
                <ol
                  key={i}
                  style={{
                    marginLeft: "20px",
                    marginBottom: "6px",
                    listStyleType:
                      block.style === "numbered" ? "decimal" : "disc"
                  }}
                >
                  {block.items.map((item, j) => (
                    <li key={j}>{renderInline(item)}</li>
                  ))}
                </ol>
              );

            default:
              return null;
          }
        })}
      </div>
    );
  }

  /* 4️⃣ Fallback */
  return <div>{String(prompt)}</div>;
}
